// @kapowx/layout-grammar — the socket's BROWSE VERB, read from the boot (ASK-154 seam 2).
//
// The verb is DERIVED by the mint beside `capacity` (forge-templates #239: `browse` + `browseSource: "derived"` on
// every resolved socket, from `domain × distribution × pose` — research §2's table), never authored and never
// re-derived here. This file only READS it and resolves it onto the universal pair:
//
//     pull-out · pick-up        — the pair every socket kind maps to (ASK-154 §1)
//     riffle · flip · play      — character verbs; on the shop floor they are a pull-out, and their character
//                                 lives inside the ONE product panel (D-076, the 2026-10-06 UX ruling)
//
// 🔴 ABSENT IS NO OPINION. An r54 boot carries no `browse`; `browseOf` returns null and every caller keeps its
// pre-verb path byte-for-byte (WoW §6: absent ≠ empty). An unknown word is REFUSED BY NAME — a verb this client
// cannot play is a reviewer-card failure to show (ASK-154 §2), never a guess.
//
// Small and eager on purpose: the composer stamps every verb-bearing seat at compose time. The motion and drag
// maths are `verb-motion.js`, which only the lazy runtime half imports.

export const BROWSE_VERBS = Object.freeze(["play", "riffle", "flip", "pick-up", "pull-out"]);
export const VERB_PAIR = Object.freeze(["pull-out", "pick-up"]);
const PAIR_OF = Object.freeze({ "pull-out": "pull-out", "pick-up": "pick-up", riffle: "pull-out", flip: "pull-out", play: "pull-out" });

/** The socket's derived verb resolved onto the pair: `{ verb, pair, source }`, `{ verb, pair:null, refused }`, or
 *  null when the boot carries none. */
export function browseOf(socket) {
	const verb = socket?.browse;
	if (verb == null) return null;
	const source = socket.browseSource ?? null;
	const pair = typeof verb === "string" && Object.hasOwn(PAIR_OF, verb) ? PAIR_OF[verb] : null;
	return pair ? { verb, pair, source } : { verb, pair: null, source, refused: `unknown browse verb '${verb}'` };
}

const XYZ = (v) => ({ x: v.x, y: v.y, z: v.z });

/**
 * The stamp ONE seat carries so the runtime can play the verb without re-reading the socket: the verb, and the
 * socket's own frame in world — the way the customer approaches it, its up, and the direction its stock is packed
 * along (a spline lane or a volume stack; null on a surface or a point, where nothing is packed along the
 * approach). null when the socket has no playable verb.
 */
export function slotBrowse(socket, { approach, frame } = {}) {
	const b = browseOf(socket);
	if (!b?.pair) return null;
	const packed = frame?.kind === "spline" || frame?.kind === "volume";
	return {
		verb: b.verb, pair: b.pair, source: b.source,
		approach: approach ? XYZ(approach) : null,
		up: frame?.normal ? XYZ(frame.normal) : { x: 0, y: 1, z: 0 },
		run: packed && frame.tangent ? XYZ(frame.tangent) : null,
	};
}
